import React, { useEffect, useState, useCallback, memo } from "react";
import axios from "axios";
import { getToken, toNumber } from "../../utils/auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import BRANDING from "../../branding";
import CONFIG from "../../config";

const AIAdmin = memo(() => {
    // eslint-disable-next-line no-unused-vars
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language.startsWith('ar');
    const navigate = useNavigate();

    // AI Config State
    const [aiConfig, setAiConfig] = useState(null);
    const [configForm, setConfigForm] = useState({
        provider: "openrouter",
        api_key: "",
        model: "mistralai/mixtral-8x7b-instruct",
        system_prompt: "You are a helpful AI assistant for a furniture store. Provide accurate, friendly, and professional responses.",
        max_tokens: 1000,
        temperature: 0.7,
        usage_tracking_enabled: 1,
        monthly_limit: ""
    });
    const [showConfigForm, setShowConfigForm] = useState(false);
    const [configLoading, setConfigLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    // Usage Statistics State
    const [usageStats, setUsageStats] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);

    // Fetch AI Config
    // const fetchAiConfig = useCallback(async () => {
    //     try {
    //         const token = getToken();
    //         const res = await axios.get(`${CONFIG.API_URL}/api/admin/ai/config`, {
    //             headers: { Authorization: `Bearer ${token}` }
    //         });
    //         setAiConfig(res.data);
    //         if (res.data) {
    //             // setConfigForm({
    //             //     provider: res.data.provider || "openrouter",
    //             //     api_key: "", // Don't populate API key for security
    //             //     model: res.data.model || "mistralai/mixtral-8x7b-instruct",
    //             //     system_prompt: res.data.system_prompt || "",
    //             //     max_tokens: res.data.max_tokens || 1000,
    //             //     temperature: res.data.temperature || 0.7,
    //             //     usage_tracking_enabled: res.data.usage_tracking_enabled || 1,
    //             //     monthly_limit: res.data.monthly_limit || ""
    //             // });
    //             setConfigForm({
    //                 provider: res.data.provider || "openrouter",
    //                 api_key: "",
    //                 model: res.data.model || "mistralai/mixtral-8x7b-instruct",
    //                 system_prompt: res.data.system_prompt || "",

    //                 // ✅ FORCE NUMBERS
    //                 max_tokens: Number(res.data.max_tokens ?? 1000),
    //                 temperature: Number(res.data.temperature ?? 0.7),
    //                 usage_tracking_enabled: Number(res.data.usage_tracking_enabled ?? 1),
    //                 monthly_limit: res.data.monthly_limit ?? ""
    //             });
    //         }
    //     } catch (err) {
    //         console.error("Failed to fetch AI config", err);
    //     }
    // }, []);
    const fetchAiConfig = useCallback(async () => {
        try {
            const token = getToken();
            const { data } = await axios.get(`${CONFIG.API_URL}/api/admin/ai/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAiConfig(data);

            if (!data) return;

            setConfigForm(prev => ({
                ...prev,
                provider: data.provider || "openrouter",
                api_key: "",
                model: data.model || prev.model,
                system_prompt: data.system_prompt || "",
                max_tokens: toNumber(data.max_tokens, 1000),
                temperature: toNumber(data.temperature, 0.7),
                usage_tracking_enabled: toNumber(data.usage_tracking_enabled, 1),
                monthly_limit: data.monthly_limit ?? ""
            }));
        } catch (err) {
            console.error("Failed to fetch AI config", err);
        }
    }, []);

    // Fetch Usage Statistics
    const fetchUsageStats = useCallback(async () => {
        setUsageLoading(true);
        try {
            const token = getToken();
            const res = await axios.get(`${CONFIG.API_URL}/api/ai/usage`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsageStats(res.data);
        } catch (err) {
            console.error("Failed to fetch usage stats", err);
        } finally {
            setUsageLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAiConfig();
        fetchUsageStats();
    }, [fetchAiConfig, fetchUsageStats]);

    // Config Handlers
    // const handleConfigChange = (e) => {
    //     const { name, value, type, checked } = e.target;
    //     setConfigForm({
    //         ...configForm,
    //         [name]: type === "checkbox" ? (checked ? 1 : 0) : value
    //     });
    // };
    const handleConfigChange = useCallback((e) => {
        if (!e?.target) return;

        const { name, value, type, checked } = e.target;

        setConfigForm(prev => {
            let newValue = value;

            if (type === "checkbox") {
                newValue = checked ? 1 : 0;
            }

            if (["max_tokens", "temperature", "monthly_limit"].includes(name)) {
                newValue = value === "" ? "" : Number(value);
            }

            return { ...prev, [name]: newValue };
        });
    }, []);

    const handleSaveConfig = async (e) => {
        e.preventDefault();
        setConfigLoading(true);
        setError("");
        setMessage("");
        try {
            const token = getToken();
            await axios.post(`${CONFIG.API_URL}/api/admin/ai/config`, configForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage("AI configuration saved successfully");
            fetchAiConfig();
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save configuration");
        } finally {
            setConfigLoading(false);
        }
    };

    const handleDeleteConfig = async () => {
        if (!window.confirm("Are you sure you want to deactivate AI configuration?")) return;
        try {
            const token = getToken();
            await axios.delete(`${CONFIG.API_URL}/api/admin/ai/config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage("AI configuration deactivated");
            fetchAiConfig();
            setTimeout(() => setMessage(""), 3000);
        } catch (err) {
            setError(err.response?.data?.message || "Failed to deactivate configuration");
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className="max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24">
            {/* Branding Header */}
            {BRANDING?.appName && (
                <div className="mb-6 pb-4 border-b">
                    <h1 className="text-3xl font-bold" style={{ color: BRANDING.colors?.primary || '#1f2937' }}>
                        {isArabic ? BRANDING.appName.ar || BRANDING.appName.en : BRANDING.appName.en}
                    </h1>
                    {BRANDING.tagline && (
                        <p className="text-gray-600 mt-1">{isArabic ? BRANDING.tagline.ar : BRANDING.tagline.en}</p>
                    )}
                </div>
            )}
            <div className="flex justify-between items-center mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.ai_admin.title')}</h2>
                    <p className="text-gray-500 mt-2 font-medium">{t('admin.ai_admin.subtitle')}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            {message && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {message}
                </div>
            )}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Configuration Section */}
            <div className="bg-white shadow rounded-lg mb-8">
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold">AI Configuration</h2>
                    {!aiConfig ? (
                        <button
                            onClick={() => setShowConfigForm(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                            Add AI Configuration
                        </button>
                    ) : (
                        <button
                            onClick={() => setShowConfigForm(true)}
                            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                        >
                            Edit Configuration
                        </button>
                    )}
                </div>

                {/* Configuration Form */}
                {showConfigForm ? (
                    <div className="bg-white shadow rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">
                            {aiConfig ? "Edit AI Configuration" : "Add AI Configuration"}
                        </h3>
                        <form onSubmit={handleSaveConfig} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">AI Provider (Free)</label>
                                    <select
                                        name="provider"
                                        value={configForm.provider}
                                        onChange={handleConfigChange}
                                        className="w-full border rounded px-3 py-2"
                                    >
                                        <option value="openrouter">OpenRouter (Free)</option>
                                        <option value="huggingface">Hugging Face (Free)</option>
                                        <option value="gemini">Google Gemini (Free)</option>
                                        <option value="openai">OpenAI (Paid)</option>
                                        <option value="anthropic">Anthropic (Paid)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Model</label>
                                    <input
                                        type="text"
                                        name="model"
                                        value={configForm.model}
                                        onChange={handleConfigChange}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="e.g., mistralai/mixtral-8x7b-instruct"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        {configForm.provider === 'openrouter' && "Recommended: mistralai/mixtral-8x7b-instruct"}
                                        {configForm.provider === 'huggingface' && "e.g., google/flan-t5-large"}
                                        {configForm.provider === 'gemini' && "e.g., gemini-1.5-flash"}
                                        {configForm.provider === 'openai' && "e.g., gpt-3.5-turbo"}
                                        {configForm.provider === 'anthropic' && "e.g., claude-3-haiku-20240307"}
                                    </p>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium mb-1">
                                        API Key {aiConfig && "(leave blank to keep existing)"}
                                    </label>
                                    <input
                                        type="password"
                                        name="api_key"
                                        value={configForm.api_key}
                                        onChange={handleConfigChange}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder={aiConfig ? "Leave blank to keep current key" : "Enter your API key"}
                                        required={!aiConfig} // Only required for new configs
                                    />
                                    {aiConfig && configForm.api_key === '' && (
                                        <p className="text-xs text-amber-600 mt-1">
                                            ℹ️ Leaving blank will keep your existing API key
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">
                                        Get your free API key from:{' '}
                                        {configForm.provider === 'openrouter' && (
                                            <a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                OpenRouter.ai
                                            </a>
                                        )}
                                        {configForm.provider === 'huggingface' && (
                                            <a href="https://huggingface.co/settings/tokens" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                Hugging Face Tokens
                                            </a>
                                        )}
                                        {configForm.provider === 'gemini' && (
                                            <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                Google AI Studio
                                            </a>
                                        )}
                                    </p>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium mb-1">System Prompt</label>
                                    <textarea
                                        name="system_prompt"
                                        value={configForm.system_prompt}
                                        onChange={handleConfigChange}
                                        rows={4}
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="You are a helpful AI assistant..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Max Tokens</label>
                                    <input
                                        type="number"
                                        name="max_tokens"
                                        value={configForm.max_tokens}
                                        onChange={handleConfigChange}
                                        min="100"
                                        max="4000"
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Temperature (0-1)</label>
                                    <input
                                        type="number"
                                        name="temperature"
                                        value={configForm.temperature}
                                        onChange={handleConfigChange}
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        className="w-full border rounded px-3 py-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Usage Tracking</label>
                                    <input
                                        type="checkbox"
                                        name="usage_tracking_enabled"
                                        checked={configForm.usage_tracking_enabled === 1}
                                        onChange={handleConfigChange}
                                        className="h-4 w-4"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Monthly Limit (optional)</label>
                                    <input
                                        type="number"
                                        name="monthly_limit"
                                        value={configForm.monthly_limit ?? ""}
                                        onChange={handleConfigChange}
                                        min="1"
                                        className="w-full border rounded px-3 py-2"
                                        placeholder="e.g., 1000"
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="submit"
                                    disabled={configLoading}
                                    className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                                >
                                    {configLoading ? "Saving..." : "Save Configuration"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowConfigForm(false);
                                        fetchAiConfig(); // Reset form
                                    }}
                                    className="bg-gray-300 text-gray-700 px-6 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                {aiConfig && (
                                    <button
                                        type="button"
                                        onClick={handleDeleteConfig}
                                        className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600"
                                    >
                                        Deactivate
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="bg-white shadow rounded-lg p-6">
                        {aiConfig ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="font-medium">Provider:</span>
                                    <p>{aiConfig.provider}</p>
                                </div>
                                <div>
                                    <span className="font-medium">Model:</span>
                                    <p>{aiConfig.model}</p>
                                </div>
                                <div>
                                    <span className="font-medium">Max Tokens:</span>
                                    <p>{aiConfig.max_tokens}</p>
                                </div>
                                <div>
                                    <span className="font-medium">Temperature:</span>
                                    <p>{aiConfig.temperature}</p>
                                </div>
                                <div className="md:col-span-2">
                                    <span className="font-medium">System Prompt:</span>
                                    <p className="mt-1 text-gray-600">{aiConfig.system_prompt || "None"}</p>
                                </div>
                                <div>
                                    <span className="font-medium">Usage Tracking:</span>
                                    <p>{aiConfig.usage_tracking_enabled ? "Enabled" : "Disabled"}</p>
                                </div>
                                {aiConfig.monthly_limit && (
                                    <div>
                                        <span className="font-medium">Monthly Limit:</span>
                                        <p>{aiConfig.monthly_limit} tasks</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-gray-500">No AI configuration found. Click "Add Configuration" to set up AI.</p>
                        )}
                    </div>
                )}

                {/* Usage Statistics */}
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Usage Statistics (Last 30 Days)</h3>
                    {usageLoading ? (
                        <p>Loading usage data...</p>
                    ) : usageStats && usageStats.recent_tasks ? (
                        <div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-blue-50 p-4 rounded">
                                    <p className="text-sm text-gray-600">Total Tasks</p>
                                    <p className="text-2xl font-bold">{usageStats.monthly?.tasks_this_month || 0}</p>
                                </div>
                                <div className="bg-green-50 p-4 rounded">
                                    <p className="text-sm text-gray-600">Total Tokens</p>
                                    <p className="text-2xl font-bold">{usageStats.monthly?.tokens_this_month || 0}</p>
                                </div>
                                <div className="bg-purple-50 p-4 rounded">
                                    <p className="text-sm text-gray-600">Est. Cost</p>
                                    <p className="text-2xl font-bold">${(Number(usageStats?.monthly?.cost_this_month) || 0).toFixed(4)}</p>
                                </div>
                            </div>

                            {usageStats.recent_tasks.length > 0 && (
                                <div className="overflow-x-auto">
                                    <h4 className="font-medium mb-2">Recent Tasks</h4>
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="px-4 py-2 text-left">Date</th>
                                                <th className="px-4 py-2 text-left">Task Type</th>
                                                <th className="px-4 py-2 text-left">Status</th>
                                                <th className="px-4 py-2 text-right">Tokens</th>
                                                <th className="px-4 py-2 text-right">Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usageStats.recent_tasks.slice(0, 20).map((task, idx) => (
                                                <tr key={idx} className="border-t">
                                                    <td className="px-4 py-2">{formatDate(task.date)}</td>
                                                    <td className="px-4 py-2">{task.task_type}</td>
                                                    <td className="px-4 py-2">
                                                        <span className={`px-2 py-1 rounded text-xs ${task.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                            {task.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2 text-right">{task.total_tokens || 0}</td>
                                                    <td className="px-4 py-2 text-right">${(Number(task.total_cost) || 0).toFixed(6)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-gray-500">No usage data available yet.</p>
                    )}
                </div>

                {/* Setup Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Free AI Provider Setup</h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm">
                        <li>
                            <strong>Choose a Free AI Provider:</strong>
                            <ul className="list-disc list-inside ml-4 mt-1">
                                <li><a href="https://openrouter.ai" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OpenRouter.ai</a> - Free tier with multiple models</li>
                                <li><a href="https://huggingface.co" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Hugging Face</a> - Free inference API</li>
                                <li><a href="https://aistudio.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google AI Studio</a> - Gemini API free</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Get Your API Key:</strong> Sign up at your chosen provider and generate an API key.
                        </li>
                        <li>
                            <strong>Configure:</strong> Enter the API key, select a model, and adjust settings above.
                        </li>
                        <li>
                            <strong>Test:</strong> Use the AI service in WhatsApp or the magic-writer-ai tool to test your configuration.
                        </li>
                    </ol>
                </div>
            </div>
        </div>
    );
});

export default AIAdmin;
